
import React, { useState, useEffect } from 'react';
import { Share } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import useAnalytics from '@/hooks/useAnalytics';

interface ShareButtonProps {
  title: string;
  url?: string; // Optional custom URL to share
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

const ShareButton = ({ 
  title, 
  url, 
  variant = "outline", 
  size = "default", 
  className 
}: ShareButtonProps) => {
  const { toast } = useToast();
  const { trackEvent } = useAnalytics();
  const [metaTitle, setMetaTitle] = useState<string>('');
  const [metaUrl, setMetaUrl] = useState<string>('');

  // Get meta information on mount
  useEffect(() => {
    // Get the title from og:title meta tag first, then document.title
    const ogTitle = document.querySelector('meta[property="og:title"]')?.getAttribute('content');
    setMetaTitle(ogTitle || document.title);

    // Get URL in this order of precedence:
    // 1. og:url meta tag
    // 2. canonical link
    // 3. Current URL
    const ogUrl = document.querySelector('meta[property="og:url"]')?.getAttribute('content');
    const canonicalUrl = document.querySelector('link[rel="canonical"]')?.getAttribute('href');
    setMetaUrl(ogUrl || canonicalUrl || window.location.href);
  }, []);

  const handleShare = async () => {
    // Determine the title to use:
    // 1. Provided title prop (if not generic "Share")
    // 2. Meta title from useEffect
    // 3. Default to document.title
    const shareTitle = (title !== 'Share') ? title : metaTitle || document.title;
    
    // Determine the URL to share:
    // 1. Explicitly provided URL prop
    // 2. Meta URL from useEffect
    // 3. Default to current URL
    const shareUrl = url || metaUrl || window.location.href;
    
    console.log('Sharing:', { title: shareTitle, url: shareUrl });
    
    const shareData = {
      title: shareTitle,
      url: shareUrl,
    };

    try {
      // Use native Web Share API if available
      if (navigator.share) {
        await navigator.share(shareData);
        trackEvent('share', { method: 'native', title: shareData.title, url: shareUrl });
      } else {
        // Fallback to copying the URL to clipboard
        await navigator.clipboard.writeText(shareUrl);
        trackEvent('share', { method: 'copy', title: shareData.title, url: shareUrl });
        
        toast({
          title: "Link copied!",
          description: "The link has been copied to your clipboard",
        });
      }
    } catch (error) {
      console.error('Error sharing content:', error);
      // Only show error toast if it's not a user cancellation
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        toast({
          title: "Sharing failed",
          description: "Unable to share this page",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <Button 
      variant={variant} 
      size={size} 
      className={className} 
      onClick={handleShare}
    >
      <Share className="h-4 w-4 mr-2" />
      Share
    </Button>
  );
};

export default ShareButton;
